import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePotwierdzenie } from '@/components/OknoPotwierdzenia';
import { Ban, Contact, Copy, KeyRound, Loader2, Plus, ShieldAlert, SlidersHorizontal, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast';
import { fetchModuleCatalog } from '@/lib/accounts-api';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import { Dialog, DialogContent, DialogFooter, ModalError } from '@/components/ui/dialog';
import {
  createPanelToken,
  listPanelTokens,
  revokePanelToken,
  zmienZakresTokenu,
  wydajKlucz,
  wydajTokenVendingPlus,
  type PanelTokenRow,
} from '@/lib/panel-tokens-api';

/**
 * Tokeny MASZYNOWE — dostęp programów do tej instancji przez `/api/v1` (dziś Vending+).
 *
 * Sekcja leży na ekranie Kont, bo ten ekran odpowiada na pytanie „kto ma dostęp do tej
 * instancji" — a token jest dostępem, tylko nie dla człowieka. Rozdzielone wizualnie od kont
 * ludzi, bo to inny rodzaj poświadczenia: nie loguje się, nie ma sesji i nie należy do nikogo.
 *
 * ⚠️ ZAKRES JEST WYMAGANY I TO JEST SEDNO TEGO EKRANU. Do 2026-08 token dawał jedno, stałe
 * uprawnienie (odczyt floty dla panelu ponad instancjami) i nie było czego wybierać. Teraz
 * decyduje o tym, co obcy system może przeczytać I ZAPISAĆ w bazie klienta, więc pola wyboru
 * są tu ważniejsze od opisu — a lista pokazuje zakres przy każdym wydanym tokenie, bo inaczej
 * „który to token" nie odpowiada na pytanie „i co on może".
 *
 * ⚠️ Plaintext pokazujemy RAZ, po utworzeniu, i nie da się go odzyskać — serwer trzyma tylko
 * skrót. Ekran musi to powiedzieć WPROST przed zamknięciem okna, inaczej pierwszą reakcją
 * na zgubiony token będzie szukanie go w portalu.
 */

/**
 * ⚠️ RĘCZNE WYDAWANIE TOKENU JEST TYMCZASOWO SCHOWANE (decyzja z 2026-08-18), nie usunięte.
 *
 * Powód: dziś jedynym odbiorcą tokenu jest Vending+, a wydanie „z ręki" wymagało opisu i pięciu
 * pól wyboru — pominięcie jednego dawało token, który przechodzi uwierzytelnienie i odbija się
 * od części tras, czyli poświadczenie wyglądające na sprawne. Formularz zostaje w kodzie razem
 * z trasą `POST /panel-tokens`, bo węższy zakres będzie potrzebny przy pierwszej integracji,
 * która nie jest Vending+. Przywrócenie to zmiana TEJ JEDNEJ stałej.
 */
const RECZNE_WYDAWANIE = false;

const STATUS_VARIANT: Record<PanelTokenRow['status'], 'success' | 'neutral' | 'warning' | 'destructive'> = {
  AKTYWNY: 'success',
  NIEUZYWANY: 'warning',
  WYGASL: 'neutral',
  ODWOLANY: 'destructive',
};

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' }) : '—');

export function PanelTokensSection() {
  const { t } = useI18n();
  const { zapytaj, oknoPytania } = usePotwierdzenie();
  const toast = useToast();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  /** plaintext świeżo wydanego tokenu — TYLKO w pamięci strony, nigdy nieodpytywany z API */
  const [fresh, setFresh] = useState<string | null>(null);
  /** Ile poprzednich tokenów zginęło przy ostatnim wydaniu — do zdania pod świeżym tokenem. */
  const [zabite, setZabite] = useState<string[]>([]);
  const [pytamOVendingPlus, setPytamOVendingPlus] = useState(false);
  const [pytamOSmartBox, setPytamOSmartBox] = useState(false);
  const [pytamOHrPlus, setPytamOHrPlus] = useState(false);
  /** Nazwa klucza HR+ z okna („HR+ Tyco") — pusta znaczy domyślną z serwera („HR+ (data)"). */
  const [hrEtykieta, setHrEtykieta] = useState('');
  /** Wyspa wybrana w oknie HR+; `null` = jeszcze nie ruszana, czyli wyspa bieżąca. */
  const [hrWyspa, setHrWyspa] = useState<string | null>(null);
  const { islands, groupId: biezacaWyspa } = useIslands();
  /**
   * ⚠️ DOMYŚLNIE WYSPA, NA KTÓREJ STOI EKRAN — a przy jednej wyspie (tak stawiamy nowe instancje)
   * wybór w ogóle się nie pokazuje. Bez wyspy serwis przy kilku wyspach i tak odmówi, zamiast
   * przypiąć klucz do pierwszej z brzegu.
   */
  const wyspaHr = hrWyspa ?? biezacaWyspa ?? (islands.length === 1 ? islands[0]!.id : null);
  const hrEtykietaZaKrotka = hrEtykieta.trim().length > 0 && hrEtykieta.trim().length < 3;
  /**
   * Token, któremu zmieniamy zakres, razem z ROBOCZYM zestawem uprawnień.
   *
   * ⚠️ ROBOCZY ZESTAW W STANIE, nie zapis przy każdym kliknięciu pola wyboru. Zapis po każdym
   * przełączeniu znaczyłby, że zdjęcie dwóch zakresów to dwa różne stany widziane przez
   * integrację — a jeden z nich to stan, którego nikt nie wybrał.
   */
  const [edycjaZakresu, setEdycjaZakresu] = useState<{
    id: string;
    label: string;
    prefix: string;
    modules: string[];
    /**
     * ⚠️ POLA DOZWOLONE DLA RODZAJU, nie cały sufit integracji: klucz SmartBoksa albo HR+ z ośmioma
     * polami wyboru zapraszał do zaznaczenia czegoś, czego serwer i tak mu nie da.
     */
    dozwolone: string[];
  } | null>(null);

  // konta zawężone do klienta nie mają tu wstępu (API pilnuje tego `SdiOnlyGuard`);
  // nie rysujemy sekcji, która i tak odmówi
  const isSdi = user?.clientId === null;

  const { data, isPending } = useQuery({ queryKey: ['panel-tokens'], queryFn: listPanelTokens, enabled: isSdi });

  /**
   * Sufit zakresu przychodzi Z SERWERA, nie jest wpisany tutaj — to ta sama lista, którą
   * egzekwuje `IntegrationTokenGuard` i którą sprawdza serwis przy wydawaniu. Skopiowana do UI
   * rozjechałaby się przy pierwszej zmianie i ekran oferowałby pola, po których serwer odmawia.
   */
  const { data: katalog } = useQuery({ queryKey: ['users', 'modules'], queryFn: fetchModuleCatalog, enabled: isSdi });
  const dozwolone = katalog?.integrationAllowed ?? [];

  const toggleScope = (key: string) =>
    setScopes((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const create = useMutation({
    mutationFn: () => createPanelToken({ label: label.trim(), modules: scopes }),
    onSuccess: (res) => {
      setFresh(res.token);
      setZabite([]);
      setLabel('');
      setScopes([]);
      void qc.invalidateQueries({ queryKey: ['panel-tokens'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  /**
   * ⚠️ JEDNO ŻĄDANIE, NIE DWA. Unieważnienie poprzednich tokenów robi serwer w tej samej
   * transakcji — patrz `wydajTokenVendingPlus`. Ekran tylko pokazuje, co zginęło.
   */
  const vendingPlus = useMutation({
    mutationFn: wydajTokenVendingPlus,
    onSuccess: (res) => {
      setFresh(res.token);
      setZabite(res.uniewaznione.map((u) => `${u.label} (${u.prefix}…)`));
      setPytamOVendingPlus(false);
      void qc.invalidateQueries({ queryKey: ['panel-tokens'] });
    },
    onError: (e: Error) => {
      setPytamOVendingPlus(false);
      toast.error(e.message);
    },
  });

  /**
   * ⚠️⚠️ OSOBNA MUTACJA, NIE PARAMETR TEJ WYŻEJ — żeby oba przyciski miały osobny stan „trwa"
   * i osobne pytanie potwierdzające. Wspólna mutacja z argumentem znaczyłaby, że kliknięcie
   * jednego blokuje drugi i że dialog potwierdzenia mówi o „kluczu" bez powiedzenia, KTÓRYM —
   * a to jest dokładnie ta pomyłka, która odcina cudzą integrację.
   */
  const smartBox = useMutation({
    mutationFn: () => wydajKlucz('SMARTBOX'),
    onSuccess: (res) => {
      setFresh(res.token);
      setZabite(res.uniewaznione.map((u) => `${u.label} (${u.prefix}…)`));
      setPytamOSmartBox(false);
      void qc.invalidateQueries({ queryKey: ['panel-tokens'] });
    },
    onError: (e: Error) => {
      setPytamOSmartBox(false);
      toast.error(e.message);
    },
  });

  /** ⚠️ Osobna mutacja z osobnym pytaniem — z tego samego powodu co przy SmartBoksie wyżej. */
  const hrPlus = useMutation({
    mutationFn: () =>
      wydajKlucz('HR_PLUS', {
        ...(hrEtykieta.trim().length > 0 ? { label: hrEtykieta.trim() } : {}),
        ...(wyspaHr !== null ? { groupId: wyspaHr } : {}),
      }),
    onSuccess: (res) => {
      setFresh(res.token);
      setZabite(res.uniewaznione.map((u) => `${u.label} (${u.prefix}…)`));
      setPytamOHrPlus(false);
      setHrEtykieta('');
      void qc.invalidateQueries({ queryKey: ['panel-tokens'] });
    },
    onError: (e: Error) => {
      setPytamOHrPlus(false);
      toast.error(e.message);
    },
  });

  /**
   * ⚠️ ZMIANA DZIAŁA NATYCHMIAST I BEZ UDZIAŁU DRUGIEJ STRONY — bramka czyta zakres z wiersza
   * przy każdym żądaniu. Dlatego to jest właściwa droga po rozszerzeniu sufitu integracji:
   * wydanie nowego tokenu zabija stary i zmusza integratora do przeklejenia sekretu.
   */
  const zmienZakres = useMutation({
    mutationFn: ({ id, modules }: { id: string; modules: string[] }) => zmienZakresTokenu(id, modules),
    onSuccess: () => {
      toast.success(t('ptok.scopeSaved'));
      setEdycjaZakresu(null);
      void qc.invalidateQueries({ queryKey: ['panel-tokens'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokePanelToken(id),
    onSuccess: () => {
      toast.success(t('ptok.revoked'));
      void qc.invalidateQueries({ queryKey: ['panel-tokens'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!isSdi) return null;

  return (
    <section className="mt-6 space-y-3 rounded-lg border border-border bg-card p-4">
      {oknoPytania}
      <header className="space-y-1">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <KeyRound className="size-4" />
          {t('ptok.title')}
        </h2>
      </header>

      {/**
        * ⚠️ JEDEN PRZYCISK, BO JEST DZIŚ JEDEN ODBIORCA. Wydanie „z ręki" wymagało opisu i pięciu
        * pól wyboru — a pominięcie jednego dawało token, który przechodzi uwierzytelnienie
        * i odbija się od części tras, czyli poświadczenie wyglądające na sprawne. Zakres liczy
        * teraz serwer z `INTEGRATION_MODULES`, więc nie ma czego pominąć.
        */}
      <div className="space-y-2 rounded-md border border-primary/30 bg-primary/5 p-3">
        <p className="text-xs font-medium">{t('ptok.vp.title')}</p>
        <Button size="sm" disabled={vendingPlus.isPending} onClick={() => setPytamOVendingPlus(true)}>
          {vendingPlus.isPending ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
          {t('ptok.vp.issue')}
        </Button>
      </div>

      {/*
        ═════════════════════════════════════════════════════════════════════════════════════
         KLUCZ SmartBox — OSOBNA RAMKA, NIE DRUGI PRZYCISK W RAMCE Vending+.

         ⚠️⚠️ DWA KLUCZE ŻYJĄ NIEZALEŻNIE i ekran musi to pokażywać układem, nie tylko tekstem.
         Wydanie klucza SmartBoksa NIE unieważnia klucza Vending+ (i odwrotnie) — gdyby oba
         przyciski stały w jednej ramce „klucze integracji", pierwsze kliknięcie w zły z nich
         wyglądałoby na to samo działanie, a odcięłoby cudzą integrację.

         ⚠️ ZDANIE MÓWI, CO TEN KLUCZ UMIE, bo to jedyna różnica, która kogoś tu interesuje:
         dwa odczyty i nic więcej.
        ═════════════════════════════════════════════════════════════════════════════════════
      */}
      {/*
        ⚠️ CZERWONA KOLORYSTYKA — na żądanie (04.09.2026): „taka jest kolorystyka tamtej strony".
        Kolor jest tu ZNAKIEM ROZPOZNAWCZYM SmartBoksa, nie ostrzeżeniem — stąd `destructive`
        użyty jako barwa marki, a nie jako sygnał awarii. Gdyby kiedyś przyszedł tu prawdziwy
        alarm, potrzebuje innego środka niż sama barwa ramki.
      */}
      <div className="space-y-2 rounded-md border border-[var(--destructive)]/30 bg-[var(--destructive)]/5 p-3">
        <p className="text-xs font-medium">{t('ptok.sb.title')}</p>
        {/*
          ⚠️ CZERWONY JAK RAMKA — na żądanie. Przycisk w kolorze domyślnym w czerwonej ramce
          wyglądał jak element z innego ekranu, wrzucony przez pomyłkę.
        */}
        <Button
          size="sm"
          variant="destructive"
          disabled={smartBox.isPending}
          onClick={() => setPytamOSmartBox(true)}
        >
          {smartBox.isPending ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
          {t('ptok.sb.issue')}
        </Button>
      </div>

      {/*
        KLUCZ HR+ (08.10.2026) — trzecia osobna ramka, z tego samego powodu co SmartBox: klucze żyją
        niezależnie, a ten jest dodatkowo przypięty do JEDNEJ wyspy (nazwę i wyspę wybiera się w oknie).
      */}
      <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
        <p className="text-xs font-medium">{t('ptok.hr.title')}</p>
        <Button size="sm" variant="outline" disabled={hrPlus.isPending} onClick={() => setPytamOHrPlus(true)}>
          {hrPlus.isPending ? <Loader2 className="size-4 animate-spin" /> : <Contact className="size-4" />}
          {t('ptok.hr.issue')}
        </Button>
      </div>

      <div className={RECZNE_WYDAWANIE ? 'space-y-3' : 'hidden'}>
        <label className="flex max-w-md flex-col gap-1 text-xs">
          <span className="text-muted-foreground">{t('ptok.label')}</span>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t('ptok.labelHint')} />
        </label>

        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">{t('ptok.scopesHint')}</p>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-5">
            {dozwolone.map((key) => (
              <label
                key={key}
                className="flex cursor-pointer items-center gap-2 rounded-md border border-border px-2 py-1.5 text-sm has-checked:border-primary has-checked:bg-primary/5"
              >
                <input
                  type="checkbox"
                  className="size-3.5 accent-[var(--primary)]"
                  checked={scopes.includes(key)}
                  onChange={() => toggleScope(key)}
                />
                <span className="truncate">{t(`nav.${key}`)}</span>
              </label>
            ))}
          </div>
        </div>

        <Button
          size="sm"
          /*
            ⚠️ PUSTY ZAKRES BLOKUJE PRZYCISK, a nie kończy się błędem z serwera. Token bez
            zakresu przechodzi uwierzytelnienie i odbija się od każdej trasy — czyli wygląda
            na sprawny i nie działa, a szukano by usterki po stronie integratora.
          */
          disabled={label.trim().length < 3 || scopes.length === 0 || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
          {t('ptok.create')}
        </Button>
      </div>

      {fresh && (
        <div className="space-y-2 rounded-md border border-[var(--warning)] bg-[color-mix(in_oklab,var(--warning)_8%,transparent)] p-3">
          <p className="flex items-center gap-2 text-xs font-medium">
            <ShieldAlert className="size-4" />
            {t('ptok.onceWarning')}
          </p>
          <code className="block overflow-x-auto rounded bg-background p-2 font-mono text-xs">{fresh}</code>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void navigator.clipboard.writeText(fresh).then(
                  () => toast.success(t('ptok.copied')),
                  // schowek bywa zablokowany (brak HTTPS, polityka przeglądarki) — wtedy token
                  // zostaje na ekranie do przepisania, a nie ginie po cichu
                  () => toast.error(t('ptok.copyFailed')),
                );
              }}
            >
              <Copy className="size-4" />
              {t('ptok.copy')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setFresh(null)}>
              {t('ptok.hide')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{t('ptok.usageHint')}</p>
          {zabite.length > 0 && (
            /**
             * ⚠️ MÓWIMY WPROST, CO PRZESTAŁO DZIAŁAĆ. Integrator dowiaduje się o unieważnieniu
             * z błędu 401 w swoim systemie; jeśli portal tego nie napisze, nikt nie skojarzy
             * jednego z drugim — a to my wydaliśmy nowy token minutę wcześniej.
             */
            <p className="text-xs text-[var(--warning)]">
              {t('ptok.vp.zabite').replace('{lista}', zabite.join(', '))}
            </p>
          )}
        </div>
      )}

      {isPending ? (
        <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
      ) : (data?.rows.length ?? 0) === 0 ? (
        <p className="rounded-md border border-border p-4 text-center text-xs text-muted-foreground">
          {t('ptok.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">{t('ptok.colLabel')}</th>
                <th className="px-3 py-2 font-medium">{t('ptok.colScopes')}</th>
                <th className="px-3 py-2 font-medium">{t('ptok.colStatus')}</th>
                <th className="px-3 py-2 font-medium">{t('ptok.colLastUsed')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('ptok.colUses')}</th>
                <th className="px-3 py-2 font-medium">{t('ptok.colCreated')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {data!.rows.map((r) => (
                <tr key={r.id} className="border-t border-border/60">
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.label}</div>
                    <code className="font-mono text-[11px] text-muted-foreground">{r.prefix}…</code>
                    {r.groupName && (
                      <div className="text-[11px] text-muted-foreground">{t('ptok.island').replace('{nazwa}', r.groupName)}</div>
                    )}
                    {/*
                      ⚠️ KLUCZ PRZYPIĘTY BEZ WYSPY — wyspę usunięto, a serwer taki klucz odrzuca. Bez znaku
                      na liście wyglądałby na „aktywny", choć integracja dostaje 401 na wszystkim.
                    */}
                    {r.islandBound === true && !r.groupId && r.revokedAt === null && (
                      <Badge variant="destructive" title={t('ptok.islandGoneHint')}>
                        {t('ptok.islandGone')}
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {r.modules.length === 0 ? (
                      /* zakres pusty = token nie wejdzie NIGDZIE; musi to być widać, bo wygląda jak sprawny */
                      <Badge variant="destructive" title={t('ptok.noScopeHint')}>{t('ptok.noScope')}</Badge>
                    ) : (
                      <span className="flex flex-wrap gap-1">
                        {r.modules.map((m) => (
                          <Badge key={m} variant="neutral">{t(`nav.${m}`)}</Badge>
                        ))}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={STATUS_VARIANT[r.status]}>{t(`ptok.status.${r.status}`)}</Badge>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{when(r.lastUsedAt)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.useCount}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {when(r.createdAt)}
                    <div className="text-[11px]">{r.createdByEmail}</div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.revokedAt === null && (
                      <span className="flex justify-end gap-1.5">
                        {/*
                          ⚠️ ZAKRES EDYTOWALNY PRZY ŻYWYM TOKENIE — dołożone 2026-08-18 po tym, jak
                          rozszerzenie sufitu integracji zostawiło Vending+ z `403` na nowych trasach.
                          Bez tego jedyną drogą było wydanie nowego tokenu, czyli unieważnienie
                          cudzego poświadczenia i przerwa w ich synchronizacji.
                        */}
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={zmienZakres.isPending}
                          onClick={() =>
                            setEdycjaZakresu({
                              id: r.id,
                              label: r.label,
                              prefix: r.prefix,
                              modules: r.modules,
                              /** Starsza instancja nie odda `kindModules` — wtedy cały sufit, jak dotąd. */
                              dozwolone: r.kindModules ?? dozwolone,
                            })
                          }
                          title={t('ptok.scopeEditHint')}
                        >
                          <SlidersHorizontal className="size-4" />
                          {t('ptok.scopeEdit')}
                        </Button>
                        {/*
                          ⚠️ PYTANIE PRZED ODWOŁANIEM — dołożone 28.08.2026 po pytaniu z wdrożenia
                          („czy wszystkie przyciski «Odwołaj» mają sprawdzenie?"). Nie miały: ten
                          odwoływał jednym kliknięciem, z samą podpowiedzią w `title`. Podpowiedzi
                          nie widzi ktoś, kto klika myszą — a odwołanie jest natychmiastowe,
                          nieodwracalne i wywala CUDZĄ integrację (Vending+), która wraca dopiero
                          po zmianie po ich stronie.

                          ⚠️ PYTANIE Z FAKTAMI, nie „jesteś pewien?": nazwa tokenu, prefiks i liczba
                          użyć. W tabeli z kilkoma tokenami to jedyna rzecz, która pozwala zauważyć,
                          że kliknięto nie ten wiersz.
                        */}
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={revoke.isPending}
                          onClick={async () => {
                            const pytanie = t('ptok.revokePytanie')
                              .replace('{label}', r.label)
                              .replace('{prefiks}', r.prefix)
                              .replace('{uzyc}', String(r.useCount));
                            const tytul = t('ptok.revoke');
                            if (!(await zapytaj({ tytul, tresc: pytanie, etykietaPotwierdz: tytul, ton: 'grozne' }))) return;
                            revoke.mutate(r.id);
                          }}
                          title={t('ptok.revokeHint')}
                        >
                          <Ban className="size-4" />
                          {t('ptok.revoke')}
                        </Button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edycjaZakresu !== null && (
        /**
         * ⚠️ OKNO, NIE EDYCJA W WIERSZU. Zmiana zakresu dotyczy poświadczenia, którym KTOŚ INNY
         * właśnie pracuje, więc ma mieć jeden świadomy moment zatwierdzenia — a nie dziać się
         * przy każdym kliknięciu w tabeli.
         */
        <Dialog open onOpenChange={(v) => !v && setEdycjaZakresu(null)}>
          <DialogContent
            title={t('ptok.scopeEditTitle')}
            description={`${edycjaZakresu.label} · ${edycjaZakresu.prefix}…`}
          >
            <div className="space-y-3">
              <p className="text-xs leading-snug text-muted-foreground">{t('ptok.scopeEditWhat')}</p>
              <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                {edycjaZakresu.dozwolone.map((key) => (
                  <label
                    key={key}
                    className="flex cursor-pointer items-center gap-2 rounded-md border border-border px-2 py-1.5 text-sm has-checked:border-primary has-checked:bg-primary/5"
                  >
                    <input
                      type="checkbox"
                      className="size-3.5 accent-[var(--primary)]"
                      checked={edycjaZakresu.modules.includes(key)}
                      onChange={() =>
                        setEdycjaZakresu((prev) =>
                          prev === null
                            ? prev
                            : {
                                ...prev,
                                modules: prev.modules.includes(key)
                                  ? prev.modules.filter((m) => m !== key)
                                  : [...prev.modules, key],
                              },
                        )
                      }
                    />
                    <span className="truncate">{t(`nav.${key}`)}</span>
                  </label>
                ))}
              </div>
              {/*
                ⚠️ PUSTY ZAKRES BLOKUJE ZAPIS, a nie kończy się błędem z serwera: token bez zakresu
                przechodzi uwierzytelnienie i odbija się od każdej trasy — czyli wygląda na sprawny
                i nie działa. Kto chce go wyłączyć, ma obok „Odwołaj".
              */}
              {edycjaZakresu.modules.length === 0 && <ModalError>{t('ptok.scopeEmpty')}</ModalError>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEdycjaZakresu(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                disabled={zmienZakres.isPending || edycjaZakresu.modules.length === 0}
                onClick={() => zmienZakres.mutate({ id: edycjaZakresu.id, modules: edycjaZakresu.modules })}
              >
                {zmienZakres.isPending ? <Loader2 className="size-4 animate-spin" /> : <SlidersHorizontal className="size-4" />}
                {t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {pytamOVendingPlus && (
        /**
         * ⚠️ POTWIERDZENIE JEST OBOWIĄZKOWE, bo ta operacja zabija poświadczenie, którym KTOŚ INNY
         * właśnie pracuje. Okno mówi więc nie tylko „czy wydać", ale i CO PRZESTANIE DZIAŁAĆ —
         * z nazwami tokenów, żeby dało się to skojarzyć z systemem po drugiej stronie.
         */
        <Dialog open onOpenChange={(v) => !v && setPytamOVendingPlus(false)}>
          <DialogContent title={t('ptok.vp.confirmTitle')} description={t('ptok.vp.confirmWhat')}>
            {(() => {
              /**
               * ⚠️⚠️ FILTR PO RODZAJU, nie sama lista aktywnych. Wymiana klucza działa per rodzaj
               * (patrz `wydajIntegracyjny`), więc okno wymieniające klucz SmartBoksa przy wydawaniu
               * Vending+ obiecywałoby odcięcie, które nie nastąpi — a to jest ta klasa nieścisłości,
               * po której nikt nie wierzy ostrzeżeniom.
               *
               * ⚠️ BRAK POLA `kind` (starsza instancja) LICZY SIĘ JAK Vending+, bo do 04.09.2026 inny
               * rodzaj nie istniał — pominięcie takich wierszy ukryłoby klucze, które NAPRAWDĘ zginą.
               */
              const aktywne = (data?.rows ?? []).filter(
                (r) =>
                  (r.status === 'AKTYWNY' || r.status === 'NIEUZYWANY') && (r.kind ?? 'VENDING_PLUS') === 'VENDING_PLUS',
              );
              return aktywne.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('ptok.vp.confirmNone')}</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm">{t('ptok.vp.confirmKills')}</p>
                  <ul className="space-y-1 text-sm">
                    {aktywne.map((r) => (
                      <li key={r.id} className="flex items-center gap-2">
                        <Ban className="size-3.5 text-destructive" />
                        <span>{r.label}</span>
                        <code className="font-mono text-[11px] text-muted-foreground">{r.prefix}…</code>
                        {r.useCount > 0 && (
                          <span className="text-[11px] text-muted-foreground">
                            {t('ptok.vp.confirmUses').replace('{ile}', String(r.useCount))}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
            <DialogFooter>
              <Button variant="outline" onClick={() => setPytamOVendingPlus(false)}>
                {t('common.cancel')}
              </Button>
              <Button disabled={vendingPlus.isPending} onClick={() => vendingPlus.mutate()}>
                {vendingPlus.isPending ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
                {t('ptok.vp.confirmDo')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {pytamOSmartBox && (
        /**
         * ⚠️ POTWIERDZENIE JEST OBOWIĄZKOWE, bo ta operacja zabija poświadczenie, którym KTOŚ INNY
         * właśnie pracuje. Okno mówi więc nie tylko „czy wydać", ale i CO PRZESTANIE DZIAŁAĆ —
         * z nazwami tokenów, żeby dało się to skojarzyć z systemem po drugiej stronie.
         */
        <Dialog open onOpenChange={(v) => !v && setPytamOSmartBox(false)}>
          <DialogContent title={t('ptok.sb.confirmTitle')} description={t('ptok.sb.confirmWhat')}>
            {(() => {
              /** ⚠️ Tylko klucze SmartBoksa — uzasadnienie w oknie Vending+ wyżej. */
              const aktywne = (data?.rows ?? []).filter(
                (r) => (r.status === 'AKTYWNY' || r.status === 'NIEUZYWANY') && r.kind === 'SMARTBOX',
              );
              return aktywne.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('ptok.sb.confirmNone')}</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm">{t('ptok.sb.confirmKills')}</p>
                  <ul className="space-y-1 text-sm">
                    {aktywne.map((r) => (
                      <li key={r.id} className="flex items-center gap-2">
                        <Ban className="size-3.5 text-destructive" />
                        <span>{r.label}</span>
                        <code className="font-mono text-[11px] text-muted-foreground">{r.prefix}…</code>
                        {r.useCount > 0 && (
                          <span className="text-[11px] text-muted-foreground">
                            {t('ptok.sb.confirmUses').replace('{ile}', String(r.useCount))}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
            <DialogFooter>
              <Button variant="outline" onClick={() => setPytamOSmartBox(false)}>
                {t('common.cancel')}
              </Button>
              <Button disabled={smartBox.isPending} onClick={() => smartBox.mutate()}>
                {smartBox.isPending ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
                {t('ptok.sb.confirmDo')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {pytamOHrPlus && (
        /**
         * ⚠️ POTWIERDZENIE Z FAKTAMI, jak przy dwóch pozostałych — ale lista ginących kluczy jest tu
         * PER WYSPA: wymiana klucza HR+ działa w obrębie (rodzaj, wyspa), więc okno pokazuje wyłącznie
         * klucze HR+ wybranej wyspy. Zmiana wyspy w oknie zmienia tę listę.
         */
        <Dialog open onOpenChange={(v) => !v && setPytamOHrPlus(false)}>
          <DialogContent title={t('ptok.hr.confirmTitle')} description={t('ptok.hr.confirmWhat')}>
            <div className="space-y-3">
              <label className="flex flex-col gap-1 text-xs">
                <span className="text-muted-foreground">{t('ptok.hr.label')}</span>
                <Input
                  value={hrEtykieta}
                  maxLength={120}
                  onChange={(e) => setHrEtykieta(e.target.value)}
                  placeholder={t('ptok.hr.labelHint')}
                />
              </label>
              {islands.length > 1 && (
                <label className="flex flex-col gap-1 text-xs">
                  <span className="text-muted-foreground">{t('ptok.hr.island')}</span>
                  <Select value={wyspaHr ?? ''} onChange={(e) => setHrWyspa(e.target.value)}>
                    {islands.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </Select>
                </label>
              )}
              {hrEtykietaZaKrotka && <ModalError>{t('ptok.hr.labelShort')}</ModalError>}
              {(() => {
                const aktywne = (data?.rows ?? []).filter(
                  (r) =>
                    (r.status === 'AKTYWNY' || r.status === 'NIEUZYWANY') && r.kind === 'HR_PLUS' && r.groupId === wyspaHr,
                );
                return aktywne.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('ptok.hr.confirmNone')}</p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm">{t('ptok.hr.confirmKills')}</p>
                    <ul className="space-y-1 text-sm">
                      {aktywne.map((r) => (
                        <li key={r.id} className="flex items-center gap-2">
                          <Ban className="size-3.5 text-destructive" />
                          <span>{r.label}</span>
                          <code className="font-mono text-[11px] text-muted-foreground">{r.prefix}…</code>
                          {r.useCount > 0 && (
                            <span className="text-[11px] text-muted-foreground">
                              {t('ptok.hr.confirmUses').replace('{ile}', String(r.useCount))}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPytamOHrPlus(false)}>
                {t('common.cancel')}
              </Button>
              <Button disabled={hrPlus.isPending || hrEtykietaZaKrotka} onClick={() => hrPlus.mutate()}>
                {hrPlus.isPending ? <Loader2 className="size-4 animate-spin" /> : <Contact className="size-4" />}
                {t('ptok.hr.confirmDo')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}
